import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import VoiceRecorder from "@/components/VoiceRecorder";
import { toast } from "sonner";
import {
  Sparkles, Loader2, PlusCircle, Save, ShieldAlert, Stethoscope, Clock, User, FlaskConical,
} from "lucide-react";
import { STANDARD_INVESTIGATIONS } from "@/lib/clinical";

const FIELDS = [
  ["examination", "General examination"],
  ["systemic", "Systemic examination"],
  ["assessment", "Assessment"],
  ["plan", "Plan"],
];

const blankNote = (kind = "regular") => ({
  id: null,
  kind,
  at: null,
  examination: "",
  systemic: "",
  assessment: "",
  plan: "",
  investigations_advised: [],
  investigations_remarks: "",
});

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso).slice(0, 16).replace("T", " "); }
}

function firstNonEmpty(...vals) {
  for (const v of vals) { if (v && v.trim()) return v.trim(); }
  return "";
}

export default function NotesTab({ patient }) {
  const [notes, setNotes] = useState([]);
  const [active, setActive] = useState(blankNote());
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dictateTarget, setDictateTarget] = useState("plan");
  const [aiLoading, setAiLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [orderAdvised, setOrderAdvised] = useState(true);
  const [orderedInv, setOrderedInv] = useState(() => new Set());
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/notes/${patient.id}`);
      setNotes(data || []);
    } catch (e) { console.warn("Notes load failed", e); }
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (payload, { silent = false } = {}) => {
    setSaving(true);
    try {
      const { data } = await api.post("/notes", { patient_id: patient.id, ...payload });
      setActive((cur) => ({ ...cur, id: data.id, at: data.at, kind: data.kind }));
      setSavedAt(new Date());
      setDirty(false);
      if (!silent) toast.success(`${data.kind === "emergency" ? "Emergency" : "Regular"} note saved`);
      // Update the notes list in-place
      setNotes((list) => {
        const idx = list.findIndex((n) => n.id === data.id);
        if (idx >= 0) { const nx = [...list]; nx[idx] = data; return nx; }
        return [data, ...list];
      });
      return data;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Save failed");
      return null;
    } finally { setSaving(false); }
  }, [patient.id]);

  // Autosave — only for an existing note (has id). New notes require explicit Save.
  const scheduleAutosave = useCallback((next) => {
    if (!next.id) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next, { silent: true }), 1200);
  }, [persist]);

  const update = (k, v) => {
    setActive((n) => {
      const nx = { ...n, [k]: v };
      setDirty(true);
      scheduleAutosave(nx);
      return nx;
    });
  };

  const changeKind = (kind) => {
    setActive((n) => {
      const nx = { ...n, kind };
      setDirty(true);
      scheduleAutosave(nx);
      return nx;
    });
  };

  const startNewNote = (kind = "regular") => {
    if (dirty && !window.confirm("Discard unsaved changes in current note?")) return;
    setActive(blankNote(kind));
    setDirty(false);
    setSavedAt(null);
  };

  const openHistoryNote = (n) => {
    if (dirty && !window.confirm("Discard unsaved changes in current note?")) return;
    setActive({
      id: n.id, kind: n.kind || "regular", at: n.at,
      examination: n.examination || "",
      systemic: n.systemic || "",
      assessment: n.assessment || "",
      plan: n.plan || "",
      investigations_advised: n.investigations_advised || [],
      investigations_remarks: n.investigations_remarks || "",
    });
    setDirty(false);
    setSavedAt(null);
  };

  const toggleAdvisedInv = (name) => {
    setActive((n) => {
      const cur = n.investigations_advised || [];
      const nx = { ...n, investigations_advised: cur.includes(name) ? cur.filter(x => x !== name) : [...cur, name] };
      setDirty(true);
      scheduleAutosave(nx);
      return nx;
    });
  };

  const saveExplicit = async () => {
    const anyContent =
      active.examination || active.systemic || active.assessment || active.plan ||
      (active.investigations_advised && active.investigations_advised.length) ||
      active.investigations_remarks;
    if (!anyContent) {
      toast.error("Nothing to save — add at least one field");
      return;
    }
    const saved = await persist(active);
    // Auto-order any newly-advised investigations as pending records.
    if (saved && orderAdvised && (active.investigations_advised || []).length) {
      const toOrder = active.investigations_advised.filter((c) => !orderedInv.has(c));
      if (toOrder.length === 0) return;
      let ok = 0;
      for (const cat of toOrder) {
        try {
          await api.post("/investigations", {
            patient_id: patient.id,
            category: cat,
            values: {},
            pending: true,
            remarks: active.investigations_remarks || "",
            advised_by_note_id: saved.id,
          });
          ok += 1;
        } catch (e) { console.warn("Order pending inv failed", cat, e); }
      }
      if (ok) {
        setOrderedInv((s) => { const nx = new Set(s); toOrder.forEach((c) => nx.add(c)); return nx; });
        toast.success(`Ordered ${ok} investigation${ok === 1 ? "" : "s"} — visible in Investigations tab`);
      }
    }
  };

  const onTranscript = (text) => {
    setActive((n) => {
      const nx = { ...n, [dictateTarget]: (n[dictateTarget] ? n[dictateTarget] + " " : "") + text.trim() };
      setDirty(true);
      scheduleAutosave(nx);
      return nx;
    });
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/round-summary/${patient.id}`);
      setActive((n) => {
        const nx = {
          ...n,
          assessment: data.assessment ? (n.assessment ? n.assessment + "\n\n— AI draft —\n" + data.assessment : data.assessment) : n.assessment,
          plan: data.plan ? (n.plan ? n.plan + "\n\n— AI draft —\n" + data.plan : data.plan) : n.plan,
        };
        setDirty(true);
        scheduleAutosave(nx);
        return nx;
      });
      toast.success("AI draft inserted — please review and edit");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally { setAiLoading(false); }
  };

  const isEmergency = active.kind === "emergency";
  const composerTitle = active.id
    ? `Editing round · ${fmtDT(active.at)}`
    : (isEmergency ? "New emergency note" : "New round note");

  return (
    <div className="space-y-5" data-testid="notes-tab">
      {/* Composer */}
      <div className={`card-clinical p-5 border-l-4 ${isEmergency ? "border-l-rose-500" : "border-l-primary/70"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl grid place-items-center ${isEmergency ? "bg-crit" : "bg-primary/10 text-primary"}`}>
              {isEmergency ? <ShieldAlert size={18} /> : <Stethoscope size={18} />}
            </div>
            <div>
              <div className="font-medium">{composerTitle}</div>
              <div className="text-[11px] text-muted-foreground">
                {active.id
                  ? (savedAt ? `Autosaved ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Loaded from history — edits autosave")
                  : "Click Save to record this note in the timeline"}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Kind toggle */}
            <div className="inline-flex rounded-full border border-border overflow-hidden text-xs" data-testid="note-kind-toggle">
              {[["regular", "Regular"], ["emergency", "Emergency"]].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => changeKind(k)}
                  className={`px-3 py-1.5 transition-colors ${
                    active.kind === k
                      ? (k === "emergency" ? "bg-rose-500 text-white" : "bg-primary text-primary-foreground")
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`note-kind-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <Button
              type="button" variant="outline" size="sm" className="rounded-full h-9"
              onClick={() => startNewNote(active.kind)} data-testid="new-note-btn"
              title="Start a fresh note"
            >
              <PlusCircle size={13} className="mr-1.5" /> New note
            </Button>
            <Button
              type="button" variant="outline" size="sm"
              onClick={runAi} disabled={aiLoading}
              className="rounded-full h-9 border-primary/40 text-primary hover:bg-primary/10"
              data-testid="ai-round-summary" title="Draft Assessment + Plan from last 24 h data"
            >
              {aiLoading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
              AI Round
            </Button>
            <VoiceRecorder onTranscript={onTranscript} />
            <Button
              type="button" size="sm" onClick={saveExplicit} disabled={saving}
              className={`rounded-full h-9 ${isEmergency ? "bg-rose-500 hover:bg-rose-600" : ""}`}
              data-testid="save-note-btn"
            >
              <Save size={13} className="mr-1.5" />
              {saving ? "Saving…" : active.id ? "Save changes" : "Save note"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">{l}</label>
                <button type="button" onClick={() => setDictateTarget(k)} className={`text-[10px] px-2 py-0.5 rounded-full border ${dictateTarget === k ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                  {dictateTarget === k ? "Voice → here" : "Aim voice"}
                </button>
              </div>
              <Textarea rows={6} value={active[k]} onChange={(e) => update(k, e.target.value)} data-testid={`notes-${k}`} placeholder={`Enter or dictate ${l.toLowerCase()}…`} />
            </div>
          ))}
        </div>

        {/* Advised investigations */}
        <div className="rounded-lg border border-border p-3 mt-4" data-testid="notes-investigations">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <FlaskConical size={13} className="text-primary" />
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Advised investigations</label>
              {(active.investigations_advised || []).length > 0 && (
                <span className="text-[10px] text-muted-foreground">({active.investigations_advised.length} selected)</span>
              )}
            </div>
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer" data-testid="order-advised-toggle">
              <input
                type="checkbox"
                checked={orderAdvised}
                onChange={(e) => setOrderAdvised(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Order these on save (creates pending entries in Investigations tab)
            </label>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STANDARD_INVESTIGATIONS.map((name) => {
              const on = (active.investigations_advised || []).includes(name);
              const ordered = orderedInv.has(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleAdvisedInv(name)}
                  data-testid={`inv-chip-${name.replace(/\s+/g, "-")}`}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    on
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                  }`}
                  title={ordered ? "Already ordered as pending" : undefined}
                >
                  {on ? "✓ " : ""}{name}{ordered && on ? " •" : ""}
                </button>
              );
            })}
          </div>
          <Textarea
            rows={2}
            value={active.investigations_remarks || ""}
            onChange={(e) => update("investigations_remarks", e.target.value)}
            data-testid="notes-inv-remarks"
            placeholder="Additional remarks — e.g., 'ABG stat, repeat CBC in 12h', 'X-ray CTB'…"
            className="mt-2"
          />
        </div>

        <div className="text-[11px] text-muted-foreground mt-3">
          AI-generated content is a draft only — always review, edit, and sign clinically.
        </div>
      </div>

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Previous rounds ({notes.length})
          </div>
        </div>

        {notes.length === 0 ? (
          <div className="card-clinical p-6 text-center text-sm text-muted-foreground" data-testid="notes-empty">
            No previous rounds. Write and save one above.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="notes-history">
            {notes.map((n) => {
              const emergency = n.kind === "emergency";
              const snippet = firstNonEmpty(n.assessment, n.plan, n.examination, n.systemic).slice(0, 200);
              const isActive = active.id === n.id;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => openHistoryNote(n)}
                    data-testid={`note-history-${n.id}`}
                    className={`w-full text-left card-clinical p-4 border-l-4 transition-colors ${
                      emergency ? "border-l-rose-500" : "border-l-primary/70"
                    } ${isActive ? "ring-2 ring-primary/40" : "hover:bg-muted/40"}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          emergency ? "bg-rose-500/15 text-rose-600 dark:text-rose-300" : "bg-primary/10 text-primary"
                        }`}>
                          {emergency ? <ShieldAlert size={10} /> : <Stethoscope size={10} />}
                          {emergency ? "Emergency" : "Round"}
                        </span>
                        <span className="text-xs tabular text-foreground flex items-center gap-1">
                          <Clock size={11} /> {fmtDT(n.at)}
                        </span>
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <User size={10} /> {n.updated_by || n.created_by || "—"}
                        </span>
                      </div>
                      {isActive && <span className="text-[10px] uppercase tracking-widest text-primary">Editing</span>}
                    </div>
                    {snippet ? (
                      <div className="text-sm text-muted-foreground line-clamp-2 whitespace-pre-wrap">{snippet}</div>
                    ) : (
                      <div className="text-sm text-muted-foreground italic">(empty)</div>
                    )}
                    {(n.investigations_advised && n.investigations_advised.length > 0) && (
                      <div className="mt-2 flex flex-wrap gap-1" data-testid={`note-${n.id}-inv-chips`}>
                        {n.investigations_advised.map((inv) => (
                          <span key={inv} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1">
                            <FlaskConical size={9} /> {inv}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
